import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import Toast from '../components/Toast';
import { useTranslation } from 'react-i18next';

// Import new modular components
import PublicAgencyHeader from '../components/public/PublicAgencyHeader';
import PublicAgencyFilters from '../components/public/PublicAgencyFilters';
import PublicAgencyTourGrid from '../components/public/PublicAgencyTourGrid';

const PublicAgencyPage = () => {
    const { t, i18n } = useTranslation();
    const { slug } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [agency, setAgency] = useState(null);
    const [tours, setTours] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    const [toast, setToast] = useState({ isVisible: false, message: '', type: 'success' });



    // Filters
    const [filters, setFilters] = useState({
        city: '',
        min_price: '',
        max_price: '',
        rooms: '',
        size: '',
        floor: '',
        property_type: '',
        apartment_id: '',
        area: '',
        include_sold: true,
        has_tour: false
    });

    const [cities, setCities] = useState([]);
    // const [areas, setAreas] = useState([]); // Removed: areas derived from city config
    const [propertyTypes, setPropertyTypes] = useState([]);
    const [defaultCurrency, setDefaultCurrency] = useState('USD');

    useEffect(() => {
        const fetchAgencyData = async () => {
            try {
                setLoading(true);
                // Fetch Agency Details
                const agencyRes = await api.get(`/agencies/public/${slug}`);
                setAgency(agencyRes.data);

                // SEO Title
                const agencyName = i18n.language === 'ar' ? (agencyRes.data.name_ar || agencyRes.data.name) : (agencyRes.data.name_en || agencyRes.data.name);
                document.title = `${t('realEstateAgency')} ${agencyName} | CasaVista360`;

                // Fetch Dropdown Data & Config
                const [citiesRes, typesRes, configRes] = await Promise.all([
                    api.get('/locations/cities'), // Changed from /agencies/cities to match ClientDashboard
                    api.get('/locations/property-types'),
                    api.get('/locations/config').catch(() => ({ data: { currency: 'USD' } }))
                ]);
                setCities(citiesRes.data);
                setPropertyTypes(typesRes.data);
                setDefaultCurrency(configRes.data.currency || 'USD');

                // Fetch Tours
                fetchTours(agencyRes.data.slug);
            } catch (err) {
                console.error("Error fetching agency data:", err);
                setError("Agency not found or error loading data.");
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchAgencyData();
        }
    }, [slug]);

    const fetchTours = async (agencySlug, currentFilters = filters) => {
        try {
            const params = {
                agency_slug: agencySlug,
                ...currentFilters
            };
            // Remove empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null) {
                    delete params[key];
                }
            });

            const response = await api.get('/tours/public', { params });
            setTours(response.data);
        } catch (err) {
            console.error("Error fetching tours:", err);
        }
    };

    // Removed: Fetch areas when city changes - now using static config
    // useEffect(() => {
    //     if (filters.city) {
    //         const fetchAreas = async () => {
    //             try {
    //                 const res = await api.get(`/tours/areas?city=${filters.city}`);
    //                 setAreas(res.data);
    //             } catch (err) {
    //                 console.error("Error fetching areas:", err);
    //             }
    //         };
    //         fetchAreas();
    //     } else {
    //         setAreas([]);
    //     }
    // }, [filters.city]);

    // Debounce timer ref
    const debounceTimer = React.useRef(null);

    const handleFilterChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;

        const newFilters = { ...filters, [name]: newValue };
        setFilters(newFilters);

        // Cancel previous timer
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        // Debounce tour fetching for text inputs
        if (['min_price', 'max_price', 'property_id', 'area'].includes(name)) { // Changed apartment_id to property_id to match state
            debounceTimer.current = setTimeout(() => {
                fetchTours(slug, newFilters);
            }, 800); // Increased debounce time slightly for better UX
        } else {
            fetchTours(slug, newFilters);
        }
    };

    const clearFilters = () => {
        const initialFilters = {
            city: '',
            min_price: '',
            max_price: '',
            rooms: '',
            size: '',
            floor: '',
            property_type: '',
            property_id: '', // Fixed naming consistency
            area: '',
            include_sold: true,
            has_tour: false
        };
        setFilters(initialFilters);
        fetchTours(slug, initialFilters);
    };

    const handleShare = (tourId) => {
        // Use /tours/{id}/view endpoint which returns OG meta tags for social sharing
        const shareUrl = `${window.location.origin}/tours/${tourId}/view?lang=${i18n.language}`;

        // Try modern clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(shareUrl).then(() => {
                setCopiedId(tourId);
                setToast({
                    isVisible: true,
                    message: t('linkCopied'),
                    type: 'success'
                });
                setTimeout(() => setCopiedId(null), 2000);
            }).catch(err => {
                console.error('Clipboard API failed, trying fallback:', err);
                // Fallback to textarea method
                fallbackCopyTextToClipboard(shareUrl, tourId);
            });
        } else {
            // Fallback for browsers that don't support clipboard API
            fallbackCopyTextToClipboard(shareUrl, tourId);
        }
    };

    const fallbackCopyTextToClipboard = (text, tourId) => {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.width = '2em';
        textArea.style.height = '2em';
        textArea.style.padding = '0';
        textArea.style.border = 'none';
        textArea.style.outline = 'none';
        textArea.style.boxShadow = 'none';
        textArea.style.background = 'transparent';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            const successful = document.execCommand('copy');
            if (successful) {
                setCopiedId(tourId);
                setToast({
                    isVisible: true,
                    message: t('linkCopied'),
                    type: 'success'
                });
                setTimeout(() => setCopiedId(null), 2000);
            } else {
                setToast({
                    isVisible: true,
                    message: t('copyFailed'),
                    type: 'error'
                });
            }
        } catch (err) {
            console.error('Fallback copy failed:', err);
            setToast({
                isVisible: true,
                message: t('copyFailed'),
                type: 'error'
            });
        }

        document.body.removeChild(textArea);
    };

    const handleWhatsAppShare = (tour) => {
        // Use /tours/{id}/view endpoint which returns OG meta tags for social sharing
        const shareUrl = `${window.location.origin}/tours/${tour.id}/view?lang=${i18n.language}`;
        const message = `${tour.title}\n${shareUrl}`;
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
    };

    if (loading) return (
        <div className="flex justify-center items-center h-screen bg-page-gradient">
            <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-gold mx-auto mb-4"></div>
                <p className="text-textMuted text-lg">{t('loading')}</p>
            </div>
        </div>
    );
    if (error) return (
        <div className="flex justify-center items-center h-screen bg-page-gradient">
            <div className="text-center">
                <p className="text-danger-500 text-xl font-semibold">{error}</p>
            </div>
        </div>
    );
    if (!agency) return null;

    return (
        <div className="min-h-screen bg-page-gradient text-text">
            <PublicAgencyHeader
                agency={agency}
                t={t}
                i18n={i18n}
            />

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col lg:flex-row gap-6">
                    <PublicAgencyFilters
                        filters={filters}
                        handleFilterChange={handleFilterChange}
                        clearFilters={clearFilters}
                        cities={cities}
                        propertyTypes={propertyTypes}
                        t={t}
                        i18n={i18n}
                    />

                    <PublicAgencyTourGrid
                        tours={tours}
                        t={t}
                        i18n={i18n}
                        handleShare={handleShare}
                        handleWhatsAppShare={handleWhatsAppShare}
                        copiedId={copiedId}
                        cities={cities}
                        propertyTypes={propertyTypes}
                        defaultCurrency={defaultCurrency}
                    />
                </div >
            </div >
            <Toast
                message={toast.message}
                type={toast.type}
                isVisible={toast.isVisible}
                onClose={() => setToast({ ...toast, isVisible: false })}
                duration={3000}
            />
        </div >
    );
};

export default PublicAgencyPage;
